import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dropdown, DropdownToggle, DropdownItem } from '../../../dropdown';
import { useDocumentState } from '../../../../contexts/DocumentContext';

enum TemplateRevisionOptions {
  edit = 'edit',
  compare = 'compare',
  published = 'published',
}

interface TemplateViewDropdownProps {
  activeTab?: 'edit' | 'compare';
  onTabChange?: (tab: 'edit' | 'compare') => void;
  onViewPublished?: () => void; // New prop for slideout action
  canManageTemplates?: boolean;
  hasPendingChanges?: boolean;
  canEdit?: boolean;
  isEditorActive?: boolean;
  hasUserEditPermission?: boolean;
  // DOCX-native in-place published view — drives the label to "Published".
  isViewingPublished?: boolean;
  // DOCX-native in-place compare view — drives the label to "Compare" and the
  // active-view state (docx-native compare is NOT the Redux isCompareModeEnabled).
  isViewingCompare?: boolean;
  // When true, this dropdown drives view via the docx state props above (not
  // the Redux compare mode used by the CKEditor path).
  isDocxNative?: boolean;
}

export const TemplateViewDropdown: React.FC<TemplateViewDropdownProps> = ({
  activeTab: _activeTab = TemplateRevisionOptions.edit,
  onTabChange,
  onViewPublished,
  canManageTemplates = true,
  hasPendingChanges: _hasPendingChanges = false,
  canEdit: _canEdit = true,
  isEditorActive: _isEditorActive = true,
  hasUserEditPermission: _hasUserEditPermission = true,
  isViewingCompare = false,
  isDocxNative = false,
  isViewingPublished = false,
}) => {
  const { state, updateDocumentState } = useDocumentState();
  const [disableToggle, setDisableToggle] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const isCompareModeEnabled = state.isCompareModeEnabled;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Determine the actual active view. DOCX-native drives view via explicit
  // props (docx compare/published are NOT the Redux isCompareModeEnabled the
  // CKEditor path uses); the CKEditor path reads Redux.
  const getActiveView = () => {
    if (isViewingPublished) return 'published';
    if (isDocxNative) return isViewingCompare ? 'compare' : 'edit';
    return isCompareModeEnabled ? 'compare' : 'edit';
  };

  const getActiveTabLabel = () => {
    const activeView = getActiveView();
    switch (activeView) {
      case 'edit':
        return 'Editor';
      case 'compare':
        return 'Compare';
      case 'published':
        return 'Published';
      default:
        return 'Editor';
    }
  };

  const handleTabSelect = async (tab: string | null) => {
    if (!tab || disableToggle) return;

    // Close the dropdown immediately
    setShowDropdown(false);

    const currentView = getActiveView();

    // Handle published as slideout action (not a tab state change)
    if (tab === 'published') {
      if (onViewPublished) {
        onViewPublished();
      }
      return;
    }

    // Handle edit/compare mode changes
    // Re-entering the current mode would construct a second editor instance.
    if (currentView === tab) {
      return;
    }

    // Update parent state for edit/compare modes (docx-native swaps its own
    // in-place Compare panel via this callback; no CKEditor RevisionHistory).
    if (onTabChange) {
      onTabChange(tab as 'edit' | 'compare');
    }

  };

  // For readonly users, just show the current view as text
  if (!canManageTemplates) {
    return (
      <div className="template-view-readonly">
        View: <span className="view-value">{getActiveTabLabel()}</span>
      </div>
    );
  }

  return (
    <Dropdown
      className="template-view-dropdown"
      drop="down"
      show={showDropdown}
      onToggle={setShowDropdown}
      onSelect={handleTabSelect}
    >
      <Dropdown.Toggle id="dropdown-filter-view" as={DropdownToggle} title="Change template view">
        <FormattedMessage
          id="template.filter.view.readout"
          defaultMessage={`View: {view}`}
          values={{
            view: <span className="view-value">{getActiveTabLabel()}</span>,
          }}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <DropdownItem highlightable>
          <div
            className="view-dropdown-item"
            onClick={() => handleTabSelect('edit')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTabSelect('edit');
              }
            }}
          >
            <FormattedMessage id="cta.edit" defaultMessage="Editor" />
          </div>
        </DropdownItem>

        <DropdownItem highlightable>
          <div
            className="view-dropdown-item"
            onClick={() => handleTabSelect('compare')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTabSelect('compare');
              }
            }}
          >
            <FormattedMessage id="cta.compare" defaultMessage="Compare" />
          </div>
        </DropdownItem>

        <DropdownItem highlightable>
          <div
            className="view-dropdown-item"
            onClick={() => handleTabSelect('published')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTabSelect('published');
              }
            }}
          >
            <FormattedMessage id="template.published" defaultMessage="Published" />
          </div>
        </DropdownItem>
      </Dropdown.Menu>
    </Dropdown>
  );
};
